import { MouseEvent, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AddCircle } from '@mui/icons-material';
import { Button } from '@mui/material';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Match } from 'effect';

import { ALLOWED_REMOTE_VIEWER_ROLES, DATE_AND_TIME_FORMAT_SHORT } from '../AppConstants';
import ActionsContextMenu from '../components/ActionsContextMenu';
import DropdownMenuCheckSelect from '../components/DropdownMenuCheckSelect';
import ErrorHandler from '../components/ErrorHandler';
import FilterScanningList from '../components/FilterScanningList';
import ScanningOverviewTable from '../components/ScanningOverviewTable';
import { TOAST_TYPE_MAP, ToastCustom } from '../components/ToastCustom';
import { userHasRoleAccess } from '../helpers/AccessManagementHelpers';
import {
    formatDateTime,
    removeUnderscoreToCapitalize,
    removeUnderscoreToUppercase
} from '../helpers/FormattingHelpers';
import { getDeleteItemModalContent } from '../helpers/ModalContentHelpers';
import { getScanStatusClassName } from '../helpers/ScanningHelpers';
import GenericModal from '../modals/GenericModal';
import { useDeleteScan } from '../services/hooks/useDeleteScan';
import { useUpdateScan } from '../services/hooks/useUpdateScan';
import { GenericModalData } from '../types/Modal';
import { OverviewMode, ScanItemTypeEnum, ScanRes } from '../types/Scan';
import { User } from '../types/User';
import ScanCreateEditModal from './ScanCreateEditModal';

import '../styles/views/ScanningOverview.less';

type TableData = ScanRes & { listItemUrl: string };

const columnHelper = createColumnHelper<TableData>();

export interface ScanningOverviewProps {
    currentPage: number | null;
    pageSize: number;
    mode: OverviewMode;
    currentUser: User;
    filterScansHandler: (criteria: string, filterVal: string) => void;
    paginatedResponse: any;
    setCurrentPage: any;
    setPageSize: any;
    filterCriteria: string;
    filterId: string;
    destinations: [];
    filterLocation: Array<string>;
    setFilterLocations: (locations: Array<string>) => void;
}

const ScanningOverview = ({
    currentPage,
    pageSize,
    mode,
    currentUser,
    filterScansHandler,
    filterCriteria,
    filterId,
    paginatedResponse,
    setCurrentPage,
    setPageSize,
    destinations,
    setFilterLocations,
    filterLocation
}: ScanningOverviewProps): ReactElement => {
    const [scanToDelete, setScanToDelete] = useState<ScanRes | null>(null);
    const [modalData, setModalData] = useState<GenericModalData | null>(null);
    const [contextMenuContent, setContextMenuContent] = useState<ScanRes | null>(null);
    const [xPos, setXPos] = useState<number | null>(null);
    const [yPos, setYPos] = useState<number | null>(null);
    const [locationOptions, setLocationOptions] = useState<Array<{ id: number; value: string }>>([]);
    const [showScanCreateEditModal, setShowScanCreateEditModal] = useState(false);
    const [scanToEdit, setScanToEdit] = useState<ScanRes>();

    const { mutate: deleteScan, isSuccess: isDeleteScanSuccess, error: deleteScanErr } = useDeleteScan();
    const { mutate: updateScan, isSuccess: isMarkAsReceivedSuccess, error: markScanAsReceivedErr } = useUpdateScan();

    // Boolean that dictates if the user has remote viewer access
    const isUserRemoteViewer = useMemo(
        () => userHasRoleAccess(currentUser, ALLOWED_REMOTE_VIEWER_ROLES),
        [currentUser]
    );

    useEffect(() => {
        if (filterLocation && filterLocation.length === 0 && locationOptions.length > 1) {
            setFilterLocations([currentUser.locationName]);
        }
    }, [locationOptions]);

    useEffect(() => {
        if (destinations) {
            setLocationOptions(
                [{ id: 0, value: 'Select all' }].concat(
                    destinations.map((destination: string, index: number) => ({
                        id: index + 1, // Make room for select all location option
                        value: destination
                    }))
                )
            );
        }
    }, [destinations]);

    useEffect(() => {
        if (isDeleteScanSuccess) {
            ToastCustom(`Scan ${scanToDelete ? scanToDelete.id : ''} successfully deleted`, TOAST_TYPE_MAP.SUCCESS);
        } else if (deleteScanErr) {
            ErrorHandler(deleteScanErr);
        }
    }, [isDeleteScanSuccess, deleteScanErr]);

    useEffect(() => {
        if (markScanAsReceivedErr) {
            ErrorHandler(markScanAsReceivedErr);
        }
        closeContextMenu();
    }, [isMarkAsReceivedSuccess, markScanAsReceivedErr]);

    const tableData = useMemo(
        () => constructTableData(paginatedResponse?.data?.items ?? [], mode),
        [paginatedResponse?.data?.items, mode]
    );

    const closeContextMenu = () => setContextMenuContent(null);

    const onActionClick = useCallback(
        (scan: ScanRes) => (event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
            setContextMenuContent(scan);
            setXPos(event.clientX);
            setYPos(event.clientY);
        },
        []
    );

    const tableColumns = useMemo(() => getScanningTableColumns(mode, onActionClick), [mode, onActionClick]);

    const table = useReactTable({
        data: tableData,
        columns: tableColumns,
        getCoreRowModel: getCoreRowModel()
    });

    const isViewingARemoteLocation =
        filterLocation?.length > 0 && (currentUser.locationName !== filterLocation[0] || filterLocation.length !== 1);

    return (
        <div className="scan-overview-container">
            <h1>{mode === 'ARRIVING_LOGGERS' ? 'Arriving Loggers' : 'Scanning'}</h1>

            <div className="top-container">
                <div className="flex-gap">
                    {mode === 'SCANNING' && (
                        <>
                            <ReactTooltip id="new-scan-tip" type="dark" effect="solid" place="bottom" />
                            <div
                                data-for="new-scan-tip"
                                data-tip={
                                    isViewingARemoteLocation ? 'Not available while viewing a remote location' : ''
                                }
                            >
                                <Button
                                    variant="contained"
                                    disabled={isViewingARemoteLocation}
                                    onClick={() => setShowScanCreateEditModal(true)}
                                    startIcon={<AddCircle />}
                                    data-for="new-scan-tip"
                                    data-tip={
                                        isViewingARemoteLocation ? 'Not available while viewing a remote location' : ''
                                    }
                                >
                                    New Scan
                                </Button>
                            </div>
                        </>
                    )}

                    {isUserRemoteViewer && (
                        <DropdownMenuCheckSelect
                            options={locationOptions}
                            checkedList={filterLocation}
                            setCheckedList={setFilterLocations}
                            fieldText={'Select locations'}
                        />
                    )}
                </div>

                <FilterScanningList
                    filterAction={filterScansHandler}
                    mode={mode}
                    initialFilterCriteria={filterCriteria}
                    initialFilterId={filterId}
                    destinations={destinations}
                />
            </div>

            {tableData && (
                <ScanningOverviewTable
                    table={table}
                    loading={paginatedResponse.isFetching}
                    totalItemsCount={paginatedResponse?.data?.count ?? 0}
                    currentPage={currentPage ? currentPage : 1}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    setCurrentPage={setCurrentPage}
                />
            )}

            {showScanCreateEditModal && (
                <ScanCreateEditModal
                    currentUser={currentUser}
                    scanToEdit={scanToEdit}
                    destinations={destinations ?? []}
                    closeMe={() => {
                        setShowScanCreateEditModal(false);
                        setScanToEdit(undefined);
                    }}
                />
            )}

            {contextMenuContent && xPos && yPos && (
                <ActionsContextMenu
                    scan={contextMenuContent}
                    currentUser={currentUser}
                    closeMe={() => closeContextMenu()}
                    editScan={(scan) => {
                        setScanToEdit(scan);
                        setShowScanCreateEditModal(true);
                    }}
                    deleteScan={(scan) => {
                        setModalData({
                            ...getDeleteItemModalContent({ id: scan.id, type: ScanItemTypeEnum.SCAN }),
                            confirmAction: () => {
                                setScanToDelete(scan);
                                deleteScan(scan.id);
                                setModalData(null);
                            },
                            closeMe: () => setModalData(null)
                        });
                    }}
                    markScan={(scanId, isReceived) => {
                        updateScan({ scanId, payload: { received: isReceived } });
                    }}
                    xPos={xPos}
                    yPos={yPos}
                    mode={mode}
                />
            )}

            <GenericModal modalData={modalData} />
        </div>
    );
};

const getScanningTableColumns = (
    mode: OverviewMode,
    onActionClick: (scan: ScanRes) => (event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => void
) => [
    columnHelper.accessor('id', { header: 'ID' }),
    Match.value(mode).pipe(
        Match.when('SCANNING', () =>
            columnHelper.accessor('scanTypeName', {
                header: 'Type',
                cell: (cellContext) => (
                    <div className="overflow-ellipsis">{removeUnderscoreToCapitalize(cellContext.getValue())}</div>
                )
            })
        ),
        Match.orElse(() =>
            columnHelper.accessor('locationName', {
                header: 'Sender',
                cell: (cellContext) => (cellContext.getValue() ? cellContext.getValue() : 'N/A')
            })
        )
    ),
    columnHelper.accessor('trackingId', {
        header: 'Tracking ID',
        cell: (cellContext) => (
            <div className="overflow-ellipsis">{cellContext.getValue() ? cellContext.getValue() : 'N/A'}</div>
        )
    }),
    columnHelper.accessor('salesOrderId', {
        header: 'Sales order ID',
        cell: (cellContext) => (
            <div className="overflow-ellipsis">{cellContext.getValue() ? cellContext.getValue() : 'N/A'}</div>
        )
    }),
    columnHelper.accessor('loggerCount', {
        header: 'Logger count',
        cell: (cellContext) => formatLoggerCount(cellContext.row.original)
    }),
    Match.value(mode).pipe(
        Match.when('SCANNING', () =>
            columnHelper.accessor('createdTime', {
                header: 'Created',
                cell: (cellContext) => (
                    <>
                        <FontAwesomeIcon icon={['far', 'clock']} id="dimgray" className="margin-right" />
                        {formatDateTime(cellContext.getValue(), DATE_AND_TIME_FORMAT_SHORT)}
                    </>
                )
            })
        ),
        Match.orElse(() =>
            columnHelper.accessor('receivedByUsername', {
                header: 'Received by',
                cell: (cellContext) => (
                    <div className="overflow-ellipsis">{cellContext.getValue() ? cellContext.getValue() : 'N/A'}</div>
                )
            })
        )
    ),
    columnHelper.accessor('statusName', {
        header: 'Status',
        cell: (cellContext) =>
            Match.value(mode).pipe(
                Match.when('SCANNING', () => (
                    <div className={`status ${getScanStatusClassName(cellContext.getValue())}`}>
                        {removeUnderscoreToUppercase(cellContext.getValue())}
                    </div>
                )),
                Match.orElse(() => (
                    <div className={`status ${cellContext.row.original.received ? 'success' : 'default'}`}>
                        {cellContext.row.original.received ? 'RECEIVED' : 'NOT RECEIVED'}
                    </div>
                ))
            )
    }),
    columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (cellContext) => (
            <button
                title="Actions"
                className="no-style"
                onClick={onActionClick(cellContext.row.original)}
                data-testid="actions-btn"
            >
                <FontAwesomeIcon icon={['far', 'ellipsis-h']} size="lg" />
            </button>
        )
    })
];

const formatLoggerCount = (scan: ScanRes) => {
    if (!Number.isNaN(scan.loggerCount)) {
        if (scan.scanTypeName?.endsWith('-BOX')) {
            return `${scan.loggerCount} in ${scan.boxCount} ${scan.boxCount > 1 ? 'boxes' : 'box'}`;
        }
        return scan.loggerCount.toString();
    }

    return 'N/A';
};

const constructTableData = (scans: Array<ScanRes>, mode: string): Array<TableData> =>
    scans.map((scan) => ({
        ...scan,
        listItemUrl: mode === 'SCANNING' ? `/scanning/${scan.id}` : ''
    }));

export default ScanningOverview;
